import { Divider } from "@mui/material";
import { AlarmIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { Avatar } from "@/components/Avatar/Avatar";
import { Button } from "@/components/Button/Button";
import { Chip } from "@/components/Chip/Chip";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { ReservationDocumentList } from "@/features/bookings/components/ReservationDocumentList";
import { type SingleFightDetailsData } from "@/features/flights/contexts/SingleFlightDetailsContext";
import type { FlightType } from "@/features/flights/types/types";
import { useOrder } from "@/hooks/useOrder";
import type { FindFlightOffersFlightStopV3 } from "@/types/codegen";
import { type Traveller } from "@/types/types";
import { parseDuration } from "@/utils/duration";

import { BaggageAllowance } from "../BaggageAllowance";
import FlightArrow from "../FlightArrow";
import { FlightInfo } from "../flights/FlightInfo";

import { TravellersListSummary } from "./TravellersListSummary";

function FlightBookSuccess({
  departureStops,
  selectedTravellers,
  departureFlight,
  returnStops,
  returnFlight,
  singleFlightDetails,
  orderId,
}: {
  departureStops: FindFlightOffersFlightStopV3[];
  selectedTravellers: Traveller[];
  returnStops: FindFlightOffersFlightStopV3[];
  departureFlight?: FlightType;
  returnFlight?: FlightType;
  singleFlightDetails?: SingleFightDetailsData;
  orderId: string;
}) {
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);

  const isLoading = isOrderLoading || !orderData?.reservations.length;

  return (
    <div className="flex w-full flex-col items-center gap-4 px-4">
      <h5 className="mt-6 mb-10 text-xl font-semibold text-black">
        {intl.formatMessage({
          defaultMessage: "Flight successfully booked!",
          description:
            "Success message shown after a flight is successfully booked",
        })}
      </h5>
      <div className="section-card flex w-full items-center justify-between gap-4">
        <span className="section-title mb-0">
          {intl.formatMessage({
            defaultMessage: "Ticket number",
            description:
              "Label for the flight ticket / booking reference number",
          })}
        </span>
        <h3 className="text-base font-semibold text-text-title">
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            orderData.reservations[0].reservationId
          )}
        </h3>
      </div>

      {orderData?.reservations.map((reservation) => (
        <ReservationDocumentList
          key={reservation.id}
          reservationId={reservation.reservationId}
        />
      ))}

      <div className="section-card flex w-full flex-col gap-4">
        {departureFlight && (
          <span className="section-title m-0">
            {intl.formatMessage({
              defaultMessage: "Departure",
              description: "Label for the departure leg of a journey",
            })}
          </span>
        )}
        {departureFlight &&
          departureFlight.segments.map((segment, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-2"
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={intl.formatMessage(
                      {
                        defaultMessage: "{airline} logo",
                        description:
                          "Accessible alt text for an airline logo image",
                      },
                      { airline: segment.marketingCarrier.airline.name },
                    )}
                    size="small"
                    src={segment.marketingCarrier.airline.logoUrl}
                    fallback={
                      segment.marketingCarrier.airline.name?.split("")[0]
                    }
                  />
                  <span className="text-xs text-text-primary">
                    {segment.marketingCarrier.airline.name ||
                      intl.formatMessage({
                        defaultMessage: "Unknown airline",
                        description:
                          "Fallback label displayed when the airline name is unavailable",
                      })}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {segment.equipment.name || segment.equipment.code}{" "}
                  {segment.marketingCarrier.flightNumber}
                </span>
              </div>

              <FlightInfo
                origin={{
                  date: segment.departure.date,
                  time: segment.departure.time,
                  code: segment.departure.airport.code,
                }}
                destination={{
                  date: segment.arrival.date,
                  time: segment.arrival.time,
                  code: segment.arrival.airport.code,
                }}
              >
                <FlightArrow overallTime={segment.duration} />
              </FlightInfo>

              {departureStops.length > 0 && departureStops[index]?.stopTime && (
                <Chip
                  icon={<AlarmIcon size={16} />}
                  color="primary"
                  label={intl.formatMessage(
                    {
                      defaultMessage:
                        "{hours}h {minutes}m stop in {cityName} ({code})",
                      description: "Layover indicator with duration and city",
                    },
                    {
                      ...parseDuration(departureStops[index].stopTime),
                      cityName: segment.arrival.city.name,
                      code: segment.arrival.airport.code,
                    },
                  )}
                />
              )}
            </div>
          ))}
        {returnFlight && (
          <span className="section-title m-0">
            {intl.formatMessage({
              defaultMessage: "Return",
              description: "Short label for the return trip type option",
            })}
          </span>
        )}
        {returnFlight &&
          returnFlight.segments.map((segment, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-4"
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={intl.formatMessage(
                      {
                        defaultMessage: "{airline} logo",
                        description:
                          "Accessible alt text for an airline logo image",
                      },
                      { airline: segment.marketingCarrier.airline.name },
                    )}
                    size="small"
                    src={segment.marketingCarrier.airline.logoUrl}
                    fallback={
                      segment.marketingCarrier.airline.name?.split("")[0]
                    }
                  />
                  <span className="text-xs text-text-primary">
                    {segment.marketingCarrier.airline.name ||
                      intl.formatMessage({
                        defaultMessage: "Unknown airline",
                        description:
                          "Fallback label displayed when the airline name is unavailable",
                      })}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {segment.equipment.name || segment.equipment.code}{" "}
                  {segment.marketingCarrier.flightNumber}
                </span>
              </div>

              <FlightInfo
                origin={{
                  date: segment.departure.date,
                  time: segment.departure.time,
                  code: segment.departure.airport.code,
                }}
                destination={{
                  date: segment.arrival.date,
                  time: segment.arrival.time,
                  code: segment.arrival.airport.code,
                }}
              >
                <FlightArrow overallTime={segment.duration} />
              </FlightInfo>

              {returnStops.length > 0 && returnStops[index]?.stopTime && (
                <Chip
                  icon={<AlarmIcon size={16} />}
                  color="primary"
                  label={intl.formatMessage(
                    {
                      defaultMessage:
                        "{hours}h {minutes}m stop in {cityName} ({code})",
                      description: "Layover indicator with duration and city",
                    },
                    {
                      ...parseDuration(returnStops[index].stopTime),
                      cityName: segment.arrival.city.name,
                      code: segment.arrival.airport.code,
                    },
                  )}
                />
              )}
            </div>
          ))}
        <Divider sx={{ margin: "1.5rem 0" }} />
        <TravellersListSummary selectedTravellers={selectedTravellers} />
        <BaggageAllowance baggageData={singleFlightDetails?.outboundBaggage} />

        {singleFlightDetails?.returnBaggage !== null ? (
          <BaggageAllowance baggageData={singleFlightDetails?.returnBaggage} />
        ) : null}
      </div>

      <div>
        <div className="section-title">
          {intl.formatMessage({
            defaultMessage: "What's next?",
            description:
              "Section heading for post-booking next steps and instructions",
          })}
        </div>
        <div className="section-card">
          <div className="flex flex-col gap-2">
            <h5 className="text-sm font-semibold text-text-primary">
              {intl.formatMessage({
                defaultMessage: "Go to My Bookings section",
                description:
                  "Button on booking confirmation to navigate to My Bookings",
              })}
            </h5>
            <span className="block text-sm font-normal text-text-primary">
              {intl.formatMessage({
                defaultMessage:
                  "Find your ticket number and use it check in on carriers page before take off.",
                description:
                  "Post-booking instruction explaining how to use the ticket number for online check-in",
              })}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-auto mb-40 flex flex-col items-center gap-2">
        <div className="mt-6 w-60">
          <Button
            fullWidth
            onClick={() => {
              navigate({ to: "/" });
            }}
            variant="contained"
            size="small"
          >
            {intl.formatMessage({
              defaultMessage: "Back to home",
              description: "Button to return to the home / search screen",
            })}
          </Button>
        </div>
        <div className="w-60">
          <Button
            fullWidth
            onClick={() => {
              navigate({ to: "/bookings", search: { category: "flights" } });
            }}
            variant="text"
            size="small"
          >
            {intl.formatMessage({
              defaultMessage: "Open My Bookings",
              description:
                "Button to navigate to the My Bookings screen after booking completion",
            })}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FlightBookSuccess;
